import {Injectable} from '@angular/core';

import {DataInput} from '../dtos/data-input';
import {FunctionRating} from '../dtos/function-rating';
import {MitarbeiterBase} from '../dtos/mitarbeiterBase';
import {
  ExportDetailErgebnis,
  Funktionsebene
} from '../dtos/result/result-m2/export-detail-ergebnis';
import {ResultDataMitarbeiter} from '../dtos/x-mitarbeiterBase';
import {DataStorageService} from './data-storage.service';
import {IndexedDbStorageService} from './storage/indexed-db-storage.service';
import {VergleichspaareSortierung} from './storage/vergleichspaare-sortierung.enum';

@Injectable({
  providedIn: 'root'
})
export class ExportDataService {
  constructor(
    public readonly storage: DataStorageService,
    private readonly idxDb: IndexedDbStorageService
  ) {}

  async getData(input?: DataInput) {
    const stdMitarbeiter = await this.storage.getMitarbeiter();
    const inputData = {
      additionalInfo: await this.storage.getAdditionalInfo(),
      enterpriseInfo: await this.storage.getEnterpriseInfo(),
      fileInfo: await this.storage.getFileInfo(),
      mitarbeiter: await this.getDataAccordingToKontrollmodus(stdMitarbeiter),
      funktionen: await this.storage.getFunktionen()
    } as DataInput;

    if (input) {
      if (inputData.mitarbeiter) {
        inputData.mitarbeiter = await this.getDataAccordingToKontrollmodus(
          input.mitarbeiter
        );
      }
      if (input.funktionen) {
        inputData.funktionen = input.funktionen;
      }
    }
    return inputData;
  }

  async getDataForComplianceRisk(
    sortierung = VergleichspaareSortierung.NachLohndifferenz
  ) {
    const complianceRiskVergleichspaareEntries =
      await this.idxDb.getVergleichspaareSortierung(sortierung);
    const complianceRiskFunktionsebeneEntries =
      await this.storage.getComplianceRiskFunktionsebene();
    const funktionen = await this.storage.getFunktionen();
    return {
      complianceRiskPersonenebene: this.mergingFunctionsToComplianceRisk(
        funktionen,
        complianceRiskVergleichspaareEntries,
        true
      ),
      ComplianceRiskFunktionsebene: {
        funktionenListe: this.mergingFunctionsToComplianceRisk(
          funktionen,
          complianceRiskFunktionsebeneEntries.funktionenListe,
          false
        ),
        gesamtesUnternehmen:
          complianceRiskFunktionsebeneEntries.gesamtesUnternehmen
      }
    };
  }

  async getDataForComplianceRiskKontrollmodus(): Promise<ExportDetailErgebnis> {
    const exportDetailErgebnis = await this.storage.getExportDetailergebnis();
    const funktionen = await this.storage.getFunktionen();
    return {
      funktionsebeneSAM: this.getfunktionsebenes(
        exportDetailErgebnis.funktionsebeneSAM,
        funktionen
      ),
      funktionsebeneNurGrundlohn: this.getfunktionsebenes(
        exportDetailErgebnis.funktionsebeneNurGrundlohn,
        funktionen
      ),
      funktionsebeneFixeGewichtung: this.getfunktionsebenes(
        exportDetailErgebnis.funktionsebeneFixeGewichtung,
        funktionen
      ),
      personenebeneSAM: this.mergingFunctionsToComplianceRisk(
        funktionen,
        exportDetailErgebnis.personenebeneSAM,
        true
      ),
      personenebeneNurGrundlohn: this.mergingFunctionsToComplianceRisk(
        funktionen,
        exportDetailErgebnis.personenebeneNurGrundlohn,
        true
      ),
      personenebeneFixeGewichtung: this.mergingFunctionsToComplianceRisk(
        funktionen,
        exportDetailErgebnis.personenebeneFixeGewichtung,
        true
      )
    } as ExportDetailErgebnis;
  }

  private getfunktionsebenes(
    funktionsebene: Funktionsebene,
    funktionen: FunctionRating[]
  ): Funktionsebene {
    return {
      gesamtesUnternehmen: funktionsebene.gesamtesUnternehmen,
      funktionenListe: this.mergingFunctionsToComplianceRisk(
        funktionen,
        funktionsebene.funktionenListe,
        false
      ).sort((a, b) => {
        const collator = new Intl.Collator([], {
          numeric: true,
          sensitivity: 'base'
        });
        return collator.compare(a.funktionId, b.funktionId);
      })
    };
  }

  private mergingFunctionsToComplianceRisk<T>(
    functionFomStorage: FunctionRating[],
    source: T[],
    isForPersonenebene: boolean
  ): T[] {
    if (functionFomStorage && source) {
      functionFomStorage.forEach(funktion => {
        if (isForPersonenebene) {
          this.setTransformedFncIdToLabel(source, funktion, 'p1Funktion');
          this.setTransformedFncIdToLabel(source, funktion, 'p2Funktion');
        } else {
          this.setTransformedFncIdToLabel(source, funktion, 'funktionId');
        }
      });
    }

    return source;
  }

  private async getDataAccordingToKontrollmodus(
    standardMitarbeiter: MitarbeiterBase[]
  ) {
    const kontrollmodus = await this.storage.getKontrollmodus();
    if (kontrollmodus) {
      const ktlMitarbeiter = await this.storage.getKontrollmodusMitarbeiter();
      return this.mergeMitarbeiterAndMitarbeiterKtlmodus(
        standardMitarbeiter,
        ktlMitarbeiter
      );
    }
    return standardMitarbeiter;
  }

  private mergeMitarbeiterAndMitarbeiterKtlmodus(
    standardMitarbeiter: MitarbeiterBase[],
    ktlmodusMitarbeiter: ResultDataMitarbeiter[]
  ) {
    const stdMitarbeiter = this.sortedBy(standardMitarbeiter, 'id');
    if (
      !ktlmodusMitarbeiter ||
      ktlmodusMitarbeiter.length !== standardMitarbeiter.length
    ) {
      return standardMitarbeiter;
    }
    const ktlMitarbeiter = this.sortedBy(ktlmodusMitarbeiter, 'id');

    return stdMitarbeiter
      .filter((item, i) => item.id === ktlMitarbeiter[i].id)
      .map((item, i) => ({...item, ...ktlMitarbeiter[i]}));
  }

  private setTransformedFncIdToLabel<TData>(
    source: TData[],
    funktion: FunctionRating,
    propName: string
  ) {
    const arr = source.filter(fnc => fnc[propName] === funktion.value);
    if (arr.length > 0) {
      arr.forEach(c => {
        c[propName] = funktion.caption;
      });
    }
    return arr;
  }

  private sortedBy<T>(source: T[], propName: string) {
    return [...source].sort((a, b) => {
      if (a[propName] < b[propName]) {
        return -1;
      }
      if (a[propName] > b[propName]) {
        return 1;
      }
      return 0;
    });
  }
}
